import { Group, Loader, Text } from '@mantine/core';
import { use } from 'react';
import Markdown from 'react-markdown';

import { ChatContext } from '../contexts/ChatContext';
import type { ChatMessageProps } from '../types';
import classes from './AssistantMessage.module.css';
import { ChatMessage } from './ChatMessage';
import { ChatErrorMessage } from '#components/chat/messages/ChatErrorMessage';
import { hasRole } from '#components/chat/utils';

export function AssistantMessage<MetadataType>({
  timestamp,
  role,
  content,
  tool_calls: toolCalls,
  sourceMessageProps,
}: ChatMessageProps<'assistant', MetadataType>) {
  const { messages } = use(ChatContext);

  const error = sourceMessageProps.error;
  if (error) {
    return (
      <ChatErrorMessage name={error.name} details={error.stack} message={error.message}>
        {error.cause?.map((cause, index) => (
          <div
            key={`${cause}-${
              // biome-ignore lint/suspicious/noArrayIndexKey: handle non-unique cause string
              index
            }`}
            className={classes.sourceError}
          >
            {cause}
          </div>
        ))}
      </ChatErrorMessage>
    );
  }

  if (toolCalls?.length) {
    const [toolCall] = toolCalls;

    const toolCallResponse = messages.find(
      (it) => it.message.role === 'tool' && it.message.tool_call_id === toolCall?.id,
    );

    const sourceToolCallIndex = messages.findLastIndex((it) =>
      hasRole(it, 'assistant') ? it.message.tool_calls?.find((it) => it.id === toolCall?.id) : undefined,
    );
    const isLatestMessage = sourceToolCallIndex === messages.length - 1;

    if (toolCallResponse || (!toolCallResponse && !isLatestMessage)) {
      return null;
    }

    return (
      <ChatMessage role={role}>
        <Group>
          <Loader color="white" size="sm" type="dots" />
          <Text fs="italic" span>
            Running tool...
          </Text>
        </Group>
      </ChatMessage>
    );
  }

  return <ChatMessage content={<Markdown>{content ?? '(no content)'}</Markdown>} role={role} timestamp={timestamp} />;
}
